import { useEffect, useState } from "react";
import api from "@/lib/api";
import { prettyType } from "@/lib/defects";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const pct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`);

const Metric = ({ label, value, hint }) => (
  <div className="border border-white/10 bg-[#121212] rounded-lg p-4" data-testid={`aistat-${label}`}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-black mt-1 text-[#C5A24A]">{value}</p>
    {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

export const AiStats = ({ projectId, refreshKey }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let on = true;
    const url = projectId ? `/ai/stats?project_id=${projectId}` : "/ai/stats";
    api.get(url).then((r) => on && setS(r.data)).catch(() => on && setS(null));
    return () => { on = false; };
  }, [projectId, refreshKey]);

  if (!s) return <div className="text-sm text-muted-foreground py-6 text-center">Loading AI accuracy…</div>;
  if (!s.total_ai && !s.missed) {
    return <div className="text-sm text-muted-foreground py-6 text-center" data-testid="aistats-empty">No AI detections reviewed yet. Run an auto-scan and approve/reject detections to build accuracy stats.</div>;
  }

  const maxType = Math.max(1, ...s.by_type.map((t) => t.approved + t.rejected + t.pending));

  return (
    <div className="space-y-5" data-testid="ai-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Precision" value={pct(s.precision)} hint="of flags that were real" />
        <Metric label="Recall" value={pct(s.recall)} hint="of real defects it caught" />
        <Metric label="F1" value={pct(s.f1)} hint="overall balance" />
        <Metric label="Hit rate" value={pct(s.hit_rate)} hint="approved / all flags" />
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-center">
        <div className="rounded-lg bg-[#121212] border border-white/10 py-3"><p className="text-xl font-bold text-[#34C759]">{s.approved}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wide">Approved</p></div>
        <div className="rounded-lg bg-[#121212] border border-white/10 py-3"><p className="text-xl font-bold text-[#FF3B30]">{s.rejected}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wide">Rejected</p></div>
        <div className="rounded-lg bg-[#121212] border border-white/10 py-3"><p className="text-xl font-bold text-blue-400">{s.duplicates || 0}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wide">Duplicate</p></div>
        <div className="rounded-lg bg-[#121212] border border-white/10 py-3"><p className="text-xl font-bold text-[#FFCC00]">{s.pending}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wide">Pending</p></div>
        <div className="rounded-lg bg-[#121212] border border-white/10 py-3"><p className="text-xl font-bold text-white">{s.missed}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wide">AI-missed</p></div>
      </div>

      {s.by_type.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">By defect type</p>
          <div className="space-y-2">
            {s.by_type.map((t) => {
              const total = t.approved + t.rejected + t.pending;
              return (
                <div key={t.type} className="flex items-center gap-3 text-xs">
                  <span className="w-28 shrink-0 truncate text-muted-foreground">{prettyType(t.type)}</span>
                  <div className="flex-1 h-4 rounded overflow-hidden bg-[#1E1E1E] flex" style={{ width: `${(total / maxType) * 100}%` }}>
                    {t.approved > 0 && <div style={{ flex: t.approved, background: "#34C759" }} />}
                    {t.rejected > 0 && <div style={{ flex: t.rejected, background: "#FF3B30" }} />}
                    {t.pending > 0 && <div style={{ flex: t.pending, background: "#3A3A3A" }} />}
                  </div>
                  <span className="w-16 text-right text-muted-foreground">{t.approved}/{total}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {s.trend.length > 1 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Reviews over time</p>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
              <LineChart data={s.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", fontSize: 12 }} />
                <Line type="monotone" dataKey="approved" stroke="#34C759" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="rejected" stroke="#FF3B30" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {Object.keys(s.reject_reasons || {}).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Why rejected</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.reject_reasons).map(([k, v]) => (
              <span key={k} className="text-[11px] px-2 py-1 rounded-full bg-[#1E1E1E] border border-white/10">{k.replace(/_/g, " ")} · {v}</span>
            ))}
          </div>
        </div>
      )}

      {(s.corrections || []).length > 0 && (
        <div data-testid="ai-corrections">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">What it actually was (reviewer notes)</p>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {s.corrections.map((c, i) => (
              <div key={i} className="text-[11px] bg-[#121212] border border-white/10 rounded p-2 leading-snug">
                <span className="text-[#FF3B30]">{(c.reason || "").replace(/_/g, " ")}</span>
                <span className="text-muted-foreground"> · {prettyType(c.type)}{c.date ? ` · ${c.date}` : ""}</span>
                <div className="text-white mt-0.5">→ {c.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiStats;
